/**
 * Setările contului: identitate, abonament + consum, securitate (parolă, sesiuni active).
 * Consumul din plan este afișat exact cum îl calculează serverul — fără estimări locale.
 */
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Field,
  Icon,
  InlineAlert,
  Input,
  PageHeader,
  Select,
  Skeleton,
  StatusDot,
  cx,
} from '../components/ui.js';
import { api } from '../lib/api.js';
import { formatDateTime, formatNumber, relativeTime, useResource } from '../lib/hooks.js';
import { errorMessage, fieldErrors, useSession } from '../lib/session.js';
import { useToast } from '../components/ui.js';
import { BrandingCard } from '../components/BrandingCard.js';

interface AccountResponse {
  account: {
    id: string;
    name: string;
    slug: string;
    billing_email: string | null;
    primary_currency: string;
    default_locale: string;
    status: string;
    restaurants_count: number;
    locations_count: number;
    members_count: number;
  };
  usage: {
    plan: {
      planCode: string;
      planName: string;
      priceCents: number;
      currency: string;
      trialDays: number;
      billingInterval: 'month' | 'year' | string;
      intervalCount: number;
      status: 'trialing' | 'active' | 'past_due' | 'paused' | 'none';
      currentPeriodEnd: string | null;
    };
    metrics: Array<{ metric: string; current: number; max: number | null }>;
  };
}

interface SessionInfo {
  id: string;
  device: string;
  ip: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string;
  isCurrent: boolean;
}

/** Prețul se formatează după moneda planului, nu cu o convenție presupusă. */
function formatPrice(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat('ro-RO', { style: 'currency', currency, maximumFractionDigits: value % 1 === 0 ? 0 : 2 }).format(value);
  } catch {
    return `${value.toFixed(0)} ${currency}`;
  }
}

/**
 * Starea abonamentului, spusă în cuvinte.
 *
 * Un cont în perioadă de probă care vede doar „Pro” nu știe ce se întâmplă la finalul perioadei;
 * unul cu plata restantă trebuie anunțat înainte de a-și pierde accesul la publicarea meniului.
 */
function planStatusLabel(status: string, periodEnd: string | null): React.ReactNode {
  if (status === 'trialing') return <Badge tone="info">Perioadă de probă</Badge>;
  if (status === 'past_due') return <Badge tone="danger">Plată restantă</Badge>;
  if (status === 'paused') return <Badge tone="warning">Abonament suspendat</Badge>;
  if (status === 'none') return <Badge tone="neutral">Fără abonament activ</Badge>;
  if (!periodEnd) return null;
  // Doar data: ora reînnoirii nu interesează pe nimeni, iar „01 oct., 00:00" arată ca o eroare.
  return (
    <span className="text-caption text-ink-500">
      Se reînnoiește la{' '}
      {new Intl.DateTimeFormat('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(periodEnd))}
    </span>
  );
}

const METRIC_LABELS: Record<string, string> = {
  restaurants: 'Restaurante',
  locations: 'Locații',
  menus: 'Meniuri',
  products: 'Produse',
  qrcodes: 'Coduri QR',
  displays: 'Ecrane',
  users: 'Utilizatori',
  storage_mb: 'Stocare (MB)',
};

const LOCALES = [
  { value: 'ro', label: 'Română' },
  { value: 'en', label: 'English' },
];

export function SettingsPage() {
  const session = useSession();
  const toast = useToast();
  const resource = useResource<AccountResponse>(() => api.get<AccountResponse>('/accounts/current'), [
    session.activeAccountId,
  ]);
  const sessions = useResource<SessionInfo[]>(() => api.get<SessionInfo[]>('/auth/sessions'), []);

  const [form, setForm] = useState({ name: '', billingEmail: '', defaultLocale: 'ro' });
  const [initialized, setInitialized] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [accountErrors, setAccountErrors] = useState<Record<string, string[]>>({});

  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string[]>>({});
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [savingPassword, setSavingPassword] = useState(false);

  if (resource.data && !initialized) {
    setInitialized(true);
    setForm({
      name: resource.data.account.name,
      billingEmail: resource.data.account.billing_email ?? '',
      defaultLocale: resource.data.account.default_locale,
    });
  }

  const canUpdateAccount = session.can('account.update');

  async function saveAccount() {
    setSavingAccount(true);
    setAccountError(null);
    setAccountErrors({});
    try {
      await api.patch('/accounts/current', {
        name: form.name.trim(),
        ...(form.billingEmail.trim() ? { billingEmail: form.billingEmail.trim().toLowerCase() } : {}),
        defaultLocale: form.defaultLocale,
      });
      toast.success('Setări salvate');
      await resource.reload();
      await session.refresh();
    } catch (error) {
      setAccountErrors(fieldErrors(error));
      setAccountError(errorMessage(error));
    } finally {
      setSavingAccount(false);
    }
  }

  async function changePassword() {
    setSavingPassword(true);
    setPasswordError(null);
    setPasswordErrors({});
    if (passwordForm.newPassword !== passwordForm.confirm) {
      setPasswordErrors({ confirm: ['Parolele nu coincid.'] });
      setSavingPassword(false);
      return;
    }
    try {
      await api.post('/auth/password/change', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      toast.success('Parolă schimbată', 'Celelalte sesiuni au fost închise din motive de securitate.');
      setPasswordForm({ currentPassword: '', newPassword: '', confirm: '' });
      await sessions.reload();
    } catch (error) {
      setPasswordErrors(fieldErrors(error));
      setPasswordError(errorMessage(error));
    } finally {
      setSavingPassword(false);
    }
  }

  async function revokeSession(id: string) {
    try {
      await api.del(`/auth/sessions/${id}`);
      toast.success('Sesiune închisă');
      await sessions.reload();
    } catch (error) {
      toast.error('Nu am putut închide sesiunea', errorMessage(error));
    }
  }

  return (
    <div className="page-enter">
      <PageHeader title="Setări" subtitle="Contul, abonamentul și securitatea." />

      <div className="mb-6">
        <BrandingCard />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Cont */}
        <Card>
          <CardHeader title="Datele contului" subtitle="Numele apare pe meniurile publice și pe ecrane." />
          {resource.loading ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-11" />
              <Skeleton className="h-11" />
              <Skeleton className="h-11" />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {accountError ? <InlineAlert tone="danger">{accountError}</InlineAlert> : null}
              <Field label="Numele contului" errors={accountErrors.name}>
                {(props) => (
                  <Input
                    {...props}
                    value={form.name}
                    disabled={!canUpdateAccount}
                    onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                  />
                )}
              </Field>
              <Field label="Email de facturare" errors={accountErrors.billingEmail} hint="Aici trimitem facturile și alertele de abonament.">
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    value={form.billingEmail}
                    disabled={!canUpdateAccount}
                    onChange={(e) => setForm((current) => ({ ...current, billingEmail: e.target.value }))}
                  />
                )}
              </Field>
              <Field label="Limba interfeței">
                {(props) => (
                  <Select
                    {...props}
                    value={form.defaultLocale}
                    disabled={!canUpdateAccount}
                    onChange={(e) => setForm((current) => ({ ...current, defaultLocale: e.target.value }))}
                  >
                    {LOCALES.map((locale) => (
                      <option key={locale.value} value={locale.value}>
                        {locale.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <div className="flex items-center justify-between gap-3">
                <span className="text-caption text-ink-400">
                  Identificator public: <span className="font-mono">{resource.data?.account.slug}</span>
                </span>
                {canUpdateAccount ? (
                  <Button loading={savingAccount} onClick={() => void saveAccount()}>
                    Salvează
                  </Button>
                ) : (
                  <Badge tone="neutral">doar proprietarul poate modifica</Badge>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Abonament */}
        <Card>
          <CardHeader
            title="Abonamentul tău"
            subtitle="Limitele se aplică automat pe fiecare acțiune."
            action={resource.data ? <Badge tone="brand">{resource.data.usage.plan.planName}</Badge> : null}
          />
          {resource.loading || !resource.data ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-8" />
              ))}
            </div>
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-baseline gap-2">
                <span className="nums font-display text-display-m text-ink-950">
                  {formatPrice(resource.data.usage.plan.priceCents / 100, resource.data.usage.plan.currency)}
                </span>
                <span className="text-body-s text-ink-600">
                  {resource.data.usage.plan.priceCents === 0
                    ? 'gratuit'
                    : `/ ${resource.data.usage.plan.billingInterval === 'year' ? 'an' : 'lună'}`}
                </span>
                {planStatusLabel(resource.data.usage.plan.status, resource.data.usage.plan.currentPeriodEnd)}
              </div>
              <ul className="flex flex-col gap-3">
                {resource.data.usage.metrics
                  .filter((metric) => metric.max !== null)
                  .map((metric) => {
                    const percent = metric.max ? Math.min(100, Math.round((metric.current / metric.max) * 100)) : 0;
                    const near = percent >= 80;
                    return (
                      <li key={metric.metric} className="flex flex-col gap-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-body-s text-ink-600">{METRIC_LABELS[metric.metric] ?? metric.metric}</span>
                          <span className={cx('nums text-caption', near ? 'text-warning' : 'text-ink-400')}>
                            {formatNumber(metric.current)} / {formatNumber(metric.max ?? 0)}
                          </span>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
                          <div
                            className={cx('h-full rounded-full', near ? 'bg-accent-500' : 'bg-brand-600')}
                            style={{ width: `${Math.max(2, percent)}%` }}
                          />
                        </div>
                      </li>
                    );
                  })}
              </ul>
              <p className="mt-4 text-caption text-ink-400">
                Planurile și limitele sunt configurabile din panoul de administrare al platformei.
              </p>
            </>
          )}
        </Card>

        {/* Securitate */}
        <Card className="lg:col-span-2">
          <CardHeader title="Securitate" subtitle="Parolă și dispozitive conectate." />
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="flex flex-col gap-4">
              <h4 className="text-body-s font-medium text-ink-950">Schimbă parola</h4>
              {passwordError ? <InlineAlert tone="danger">{passwordError}</InlineAlert> : null}
              <Field label="Parola actuală" errors={passwordErrors.currentPassword}>
                {(props) => (
                  <Input
                    {...props}
                    type="password"
                    autoComplete="current-password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm((current) => ({ ...current, currentPassword: e.target.value }))}
                  />
                )}
              </Field>
              <Field label="Parola nouă" errors={passwordErrors.newPassword} hint="Minim 10 caractere.">
                {(props) => (
                  <Input
                    {...props}
                    type="password"
                    autoComplete="new-password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm((current) => ({ ...current, newPassword: e.target.value }))}
                  />
                )}
              </Field>
              <Field label="Confirmă parola nouă" errors={passwordErrors.confirm}>
                {(props) => (
                  <Input
                    {...props}
                    type="password"
                    autoComplete="new-password"
                    value={passwordForm.confirm}
                    onChange={(e) => setPasswordForm((current) => ({ ...current, confirm: e.target.value }))}
                  />
                )}
              </Field>
              <div>
                <Button
                  loading={savingPassword}
                  disabled={!passwordForm.currentPassword || passwordForm.newPassword.length < 10}
                  onClick={() => void changePassword()}
                >
                  Schimbă parola
                </Button>
              </div>
              <p className="text-caption text-ink-400">
                Autentificarea în doi pași (2FA) este pregătită în platformă și se activează din configurare.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              <h4 className="text-body-s font-medium text-ink-950">Dispozitive conectate</h4>
              {sessions.loading ? (
                <div className="flex flex-col gap-2">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton key={index} className="h-12" />
                  ))}
                </div>
              ) : (
                <ul className="flex flex-col divide-y divide-ink-100">
                  {(sessions.data ?? []).map((item) => (
                    <li key={item.id} className="flex items-center justify-between gap-3 py-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <StatusDot tone={item.isCurrent ? 'success' : 'neutral'} />
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate text-body-s text-ink-950">
                            {item.device}
                            {item.isCurrent ? <span className="ml-2 text-caption text-brand-600">acest dispozitiv</span> : null}
                          </span>
                          <span className="text-caption text-ink-400">
                            {item.ip ?? 'IP necunoscut'} · ultima activitate{' '}
                            {item.lastUsedAt ? relativeTime(item.lastUsedAt) : formatDateTime(item.createdAt)}
                          </span>
                        </div>
                      </div>
                      {!item.isCurrent ? (
                        <Button variant="ghost" size="sm" onClick={() => void revokeSession(item.id)}>
                          Închide
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex items-center gap-2 text-caption text-ink-400">
                <Icon name="shield" size={15} />
                Sesiunile expiră automat și pot fi închise oricând de aici.
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
